import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';

import FlowerSparkles from './FlowerSparkles';
import Store from '../Store';



// Make sure to define easeInOutCubic function or import it if you're using it
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
}


class Flower {
  constructor(flowerModel, point) {
    const { scene } = Store.getState();

    this.mesh = flowerModel.scene.clone();
    this.mesh.position.copy(point);

    // Randomize scale between 0.5 and 1.0
    this.randomScale = 0.7 + Math.random() * 1.5;
    this.mesh.scale.set(0, 0, 0);

    // Randomize rotation only around y-axis (spin in place)
    this.mesh.rotation.y = Math.random() * 2 * Math.PI;

    scene.add(this.mesh);

    // Animation durations
    this.growDuration = 1000;
    this.dieDuration = 1000;

    this.sparkles = new FlowerSparkles(this);

    this.grow();
  }

  scaleOverTime(targetScale, duration, onComplete) {
    const startScale = this.mesh.scale.x;
    const scaleChange = targetScale - startScale;
    const startTime = Date.now();

    const intervalId = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const progress = elapsedTime / duration;
      if (progress < 1) {
        const easedProgress = easeInOutCubic(progress);
        const newScale = startScale + easedProgress * scaleChange;
        this.mesh.scale.set(newScale, newScale, newScale);
      } else {
        this.mesh.scale.set(targetScale, targetScale, targetScale);
        clearInterval(intervalId);
        if (onComplete) {
          onComplete();
        }
      }
    }, 20);
  }

  grow() {
    this.scaleOverTime(this.randomScale, this.growDuration);
  }

  die() {
    this.scaleOverTime(0, this.dieDuration, () => {
      // When the die animation completes, perform cleanup
      this.cleanup();
    });
  }

  cleanup() {
    // Perform any cleanup necessary for the flower dying here
    if (this.sparkles) {
      this.sparkles.dispose(); // Assuming a dispose method exists to clean up sparkles
      delete this.sparkles; // Remove the sparkles property
    }

    // Remove the mesh from the scene, etc.
    const { scene } = Store.getState();
    if (scene && this.mesh) {
      scene.remove(this.mesh);
    }
  }
}

export default class Flowers {
  constructor() {
    this.flowers = [];
    this.models = [];
    this.clock = new THREE.Clock();
    this.scaleAnimations = [];
    this.lastFlowerTimestamp = 0;  // For cooldown
    this.positionMap = new Map(); // A map to track occupied position

    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://static.mused.org/spaceshare/draco1.5.6/');
    loader.setDRACOLoader(dracoLoader);
    this.granularity = 0.1;

    // preload model files 
    const flowerURLs = [
      "https://static.mused.org/flower_red_v2.glb",
      "https://static.mused.org/flower_yellow.glb",
      "https://static.mused.org/flower_white.glb",
      "https://static.mused.org/flower_leaf.glb",
    ];
    flowerURLs.forEach(url => {
      loader.load(url, gltf => {
        this.models.push(gltf);
      }, undefined, error => {
        console.error('An error occurred while loading the model:', error);
      });
    });
  }

  // Converts a point to a string key, with granularity
  pointToKey(point) {
    const granularity = this.granularity;
    const x = Math.floor(point.x / granularity) * granularity;
    const y = Math.floor(point.y / granularity) * granularity;
    const z = Math.floor(point.z / granularity) * granularity;
    return `${x}_${y}_${z}`;
  }

  // Check if nearby positions are also free
  isAreaFree(point) {
    const key = this.pointToKey(point);
    const granularity = this.granularity;
    const keysToCheck = [
      key,
      this.pointToKey({ x: point.x + granularity, y: point.y, z: point.z }),
      this.pointToKey({ x: point.x - granularity, y: point.y, z: point.z }),
      this.pointToKey({ x: point.x, y: point.y, z: point.z + granularity }),
      this.pointToKey({ x: point.x, y: point.y, z: point.z - granularity }),
      // Check diagonal directions as well
      this.pointToKey({ x: point.x + granularity, y: point.y, z: point.z + granularity }),
      this.pointToKey({ x: point.x - granularity, y: point.y, z: point.z + granularity }),
      this.pointToKey({ x: point.x + granularity, y: point.y, z: point.z - granularity }),
      this.pointToKey({ x: point.x - granularity, y: point.y, z: point.z - granularity }),
    ];
    return keysToCheck.every(k => !this.positionMap.has(k));
  }

  makeFlower(point) {
    const { app, tourPointActiveIdx } = Store.getState();
    const currentTime = this.clock.getElapsedTime();

    if (currentTime - this.lastFlowerTimestamp < 0.1) {
      return;  // Do not create flower, cooldown active
    }

    if (tourPointActiveIdx < 6) {
      return;
    }

    // only spawn the flowers on the floor 
    // Check if point is on the floor and area is not occupied
    if (point.y < 0 && this.isAreaFree(point)) {
        const randFlowerIdx = Math.floor(Math.random() * this.models.length);
        const flower = new Flower(this.models[randFlowerIdx], point);
        this.flowers.push(flower);
        this.positionMap.set(this.pointToKey(point), true); // Mark position as occupied
        this.lastFlowerTimestamp = currentTime;  

        if (this.flowers.length > 30) {
          const oldestFlower = this.flowers.shift();
          oldestFlower.die();
        }
      }
  }

  update() {
    this.flowers.forEach(flower => {
      if(flower.sparkles) {
        flower.sparkles.update();
      }
    });
  }
}
